/// Route table with the 120ms fade used by mobile's go_router transitions.
/// Redirect order: remote gates, then offline. Web skips the mobile onboarding flow.

import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';

import { ForceUpdateScreen } from '@/app/remote_config/ui/ForceUpdateScreen';
import { MaintenanceScreen } from '@/app/remote_config/ui/MaintenanceScreen';
import { ServerUnavailableScreen } from '@/app/remote_config/ui/ServerUnavailableScreen';
import { useAppGate, useServerReachable } from '@/app/remote_config/handler/remoteConfigStore';
import { appContentMaxWidth } from '@/app/ui/appContentWidth';
import { NotFoundScreen } from '@/app/ui/NotFoundScreen';
import { AuthScreen } from '@/auth/ui/AuthScreen';
import { ResetPasswordScreen } from '@/auth/ui/ResetPasswordScreen';
import { VerifyEmailScreen } from '@/auth/ui/VerifyEmailScreen';
import { OfflineScreen } from '@/connectivity/ui/OfflineScreen';
import { useConnectivityStore } from '@/connectivity/handler/connectivityStore';
import { useGameSessionStore } from '@/game/handler/gameSessionStore';
import { GameScreen } from '@/game/ui/GameScreen';
import { StatsPage } from '@/game/ui/StatsPage';
import { RankPage } from '@/rank/ui/RankPage';
import { SettingsPage } from '@/settings/ui/SettingsPage';
import { SupportScreen } from '@/support/ui/SupportScreen';
import { ChangeEmailScreen } from '@/user/ui/ChangeEmailScreen';
import { ChangePasswordScreen } from '@/user/ui/ChangePasswordScreen';
import { ChangeUsernameScreen } from '@/user/ui/ChangeUsernameScreen';
import { UserScreen } from '@/user/ui/UserScreen';
import { GuestScoreMergeHost } from '@/user/ui/GuestScoreMergeHost';
import { AutoUsernamePromptHost } from '@/user/ui/AutoUsernamePromptHost';
import { appRoutes, routeTransitionMs } from './routes';

export function AppRouter() {
  const location = useLocation();
  useAppRedirects();

  const onGameHome = location.pathname === appRoutes.home;

  return (
    <>
      <div
        key={location.pathname}
        className="h-full min-h-full"
        style={{
          animation: `dinodash-route-in ${routeTransitionMs}ms ease-out`,
          ...(onGameHome
            ? undefined
            : { maxWidth: appContentMaxWidth, marginLeft: 'auto', marginRight: 'auto' }),
        }}
      >
        <Routes location={location}>
          <Route path={appRoutes.home} element={<GameScreen />} />
          <Route path={appRoutes.settings} element={<SettingsPage />} />
          <Route path={appRoutes.rank} element={<RankPage />} />
          <Route path={appRoutes.stats} element={<StatsPage />} />
          <Route path={appRoutes.auth} element={<AuthScreen />} />
          <Route path={appRoutes.verifyEmail} element={<VerifyEmailScreen />} />
          <Route path={appRoutes.resetPassword} element={<ResetPasswordScreen />} />
          <Route path={appRoutes.user} element={<UserScreen />} />
          <Route path={appRoutes.changeUsername} element={<ChangeUsernameScreen />} />
          <Route path={appRoutes.changeEmail} element={<ChangeEmailScreen />} />
          <Route path={appRoutes.changePassword} element={<ChangePasswordScreen />} />
          <Route path={appRoutes.support} element={<SupportScreen />} />
          <Route path={appRoutes.offline} element={<OfflineScreen />} />
          <Route path={appRoutes.serverUnavailable} element={<ServerUnavailableScreen />} />
          <Route path={appRoutes.maintenance} element={<MaintenanceScreen />} />
          <Route path={appRoutes.forceUpdate} element={<ForceUpdateScreen />} />
          <Route path={appRoutes.notFound} element={<NotFoundScreen />} />
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      </div>
      <GuestScoreMergeHost />
      <AutoUsernamePromptHost />
    </>
  );
}

function useAppRedirects(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const gate = useAppGate();
  const online = useConnectivityStore((s) => s.online);
  const serverReachable = useServerReachable();
  const inGame = useGameSessionStore((s) => s.inGame);
  const loc = location.pathname;

  useEffect(() => {
    const onMaintenance = loc === appRoutes.maintenance;
    const onForceUpdate = loc === appRoutes.forceUpdate;
    const onOffline = loc === appRoutes.offline;
    const onServerUnavailable = loc === appRoutes.serverUnavailable;

    if (gate === 'maintenance') {
      if (!onMaintenance && !inGame) navigate(appRoutes.maintenance, { replace: true });
      return;
    }
    if (gate === 'forceUpdate') {
      if (!onForceUpdate && !inGame) navigate(appRoutes.forceUpdate, { replace: true });
      return;
    }
    if (onMaintenance || onForceUpdate) {
      navigate(appRoutes.home, { replace: true });
      return;
    }

    if (!online && !inGame && !onOffline) {
      navigate(appRoutes.offline, { replace: true });
      return;
    }
    if (online && onOffline) {
      navigate(serverReachable ? appRoutes.home : appRoutes.serverUnavailable, { replace: true });
      return;
    }

    if (online && !serverReachable && !inGame && !onServerUnavailable) {
      navigate(appRoutes.serverUnavailable, { replace: true });
      return;
    }
    if (serverReachable && onServerUnavailable) {
      navigate(appRoutes.home, { replace: true });
    }
  }, [gate, inGame, loc, navigate, online, serverReachable]);
}
